import { UnexpectedError } from "@grafit/shared/domain/error/errors";
import { alertDevs } from "@grafit/shared/observability/sentry-front";
import type { z } from "zod";

export function readFromStorage<TSchema extends z.ZodType>({
  key,
  initialValue,
  schema,
}: {
  key: string;
  initialValue: z.infer<TSchema>;
  schema: TSchema;
}): z.infer<TSchema> {
  let item: string | null | undefined;
  try {
    item = window.localStorage.getItem(key);
    if (!item) {
      return initialValue;
    }

    const raw: unknown = JSON.parse(item);

    const result = schema.safeParse(raw);
    if (result.success) {
      return result.data;
    }

    alertDevs({
      error: new UnexpectedError("Stored value does not match expected schema", { cause: result.error }),
      context: { key, item },
    });
    removeFromStorage({ key });
    return initialValue;
  } catch (e: unknown) {
    alertDevs({
      error: new UnexpectedError("Failed to parse item from localStorage", { cause: e }),
      context: { key, item },
    });
    removeFromStorage({ key });
    return initialValue;
  }
}

export function writeToStorage({ key, value }: { key: string; value: unknown }): void {
  try {
    window.localStorage.setItem(key, JSON.stringify(value));
  } catch (e: unknown) {
    alertDevs({
      error: new UnexpectedError("Failed to store item in localStorage", { cause: e }),
      context: { key },
    });
  }
}

export function removeFromStorage({ key }: { key: string }): void {
  try {
    window.localStorage.removeItem(key);
  } catch {
    // Removing is best-effort: a storage that refuses writes leaves a stale entry at worst.
  }
}
